import { broadcastTx, getBtcFeeRate, getConfirmedUtxos, getProvider, selectUtxos } from '@optimex-xyz/trade-wallet-sdk'
import TrezorConnect, { EthereumSignTransaction, Params, RefTransaction } from '@trezor/connect-web'
import type { PROTO } from '@trezor/connect/lib/constants'
import { ProtoWithDerivationPath } from '@trezor/connect/lib/types/params'

import { INetwork } from '@/types'
import { isBtcChain, isEvmChain } from '@/utils'
import { APP_URL, SUPPORTED_NETWORK, TREZOR_EMAIL, WALLET_TREZOR } from '@/utils/constants'

import { ethers } from 'ethers'

import { BaseWallet } from './BaseWallet'
import { IWalletConnectOptions, PsbtSignOptions, TransactionOptions } from './types'

type BitcoinInputScriptType = 'SPENDADDRESS' | 'SPENDMULTISIG' | 'SPENDWITNESS' | 'SPENDP2SHWITNESS' | 'SPENDTAPROOT'
type BitcoinOutputScriptType = 'PAYTOADDRESS' | 'PAYTOMULTISIG' | 'PAYTOWITNESS' | 'PAYTOP2SHWITNESS' | 'PAYTOTAPROOT'

export class TrezorWallet extends BaseWallet {
  name = 'Trezor Wallet'
  key = WALLET_TREZOR

  private connectedAddresses: Record<string, string> = {}
  private connectedCompressedPublicKey: Record<string, string> = {}
  private provider: ethers.JsonRpcProvider | undefined
  private path: string = ''

  constructor() {
    super()
    TrezorConnect.init({
      lazyLoad: true,
      manifest: {
        email: TREZOR_EMAIL,
        appUrl: APP_URL,
      },
    })
  }

  private getNetworkParams(network: string, path: string): { path: string; coin: string } {
    if (isEvmChain(network)) {
      return { path, coin: 'eth' }
    }
    switch (network) {
      case SUPPORTED_NETWORK.BTC:
        return { path, coin: 'btc' }
      case SUPPORTED_NETWORK.BTC_TESTNET:
        return { path, coin: 'testnet' }
      default:
        throw new Error(`Unsupported network: ${network}`)
    }
  }

  async connect(
    networkId: string,
    options: IWalletConnectOptions
  ): Promise<{ address: string; networkInfo: INetwork; compressedPublicKey: string | undefined }> {
    if (!options.path) throw new Error('Path is required')
    const { path: defaultPath, coin } = this.getNetworkParams(networkId, options.path)
    this.path = options?.path || defaultPath

    let result
    let pubKeyResult

    if (coin === 'eth') {
      this.provider = getProvider(networkId)
      result = await TrezorConnect.ethereumGetAddress({ path: this.path, showOnTrezor: true })
    } else {
      // Bitcoin case, different address between testnet and mainnet, so we need to pass coin arguments
      result = await TrezorConnect.getAddress({ path: this.path, coin, showOnTrezor: true })
      pubKeyResult = await TrezorConnect.getPublicKey({
        path: this.path,
        coin,
        suppressBackupWarning: true,
      })
      if (pubKeyResult.success) {
        this.connectedCompressedPublicKey[networkId] = pubKeyResult.payload.publicKey
      }
    }

    if (result.success) {
      const address = result.payload.address
      this.connectedAddresses[networkId] = address
      return {
        address,
        networkInfo: options.network,
        compressedPublicKey: pubKeyResult?.success ? pubKeyResult.payload.publicKey : undefined,
      }
    }
    // Get address failed, connected failed, throw error
    throw new Error(result.payload.error)
  }

  async disconnect(): Promise<void> {
    TrezorConnect.dispose()
    this.connectedAddresses = {}
    this.connectedCompressedPublicKey = {}
  }

  async sendTransaction(
    toAddress: string,
    amount: number | bigint,
    networkId: string,
    options?: TransactionOptions
  ): Promise<string> {
    if (!networkId) {
      throw new Error('Network must be specified for sending transaction')
    }
    if (isEvmChain(networkId)) return this.sendEthereumTransaction(toAddress, amount, networkId, options)
    if (isBtcChain(networkId)) return this.sendBitcoinTransaction(toAddress, Number(amount), networkId, options)

    throw new Error(`Unsupported network for sending transaction: ${networkId}`)
  }

  private async sendEthereumTransaction(
    toAddress: string,
    amount: number | bigint,
    networkId: string,
    options?: TransactionOptions
  ): Promise<string> {
    this.provider = getProvider(networkId)
    if (!this.provider) {
      throw new Error(`Nofound provider`)
    }
    const { path } = this.getNetworkParams(networkId, this.path)

    const address = this.connectedAddresses[networkId]
    if (!address) {
      throw new Error('Wallet not connected for this network')
    }

    const [nonce, feeData, networkInfo] = await Promise.all([
      this.provider.getTransactionCount(address),
      this.provider.getFeeData(),
      this.provider.getNetwork(),
    ])

    let transaction: Params<EthereumSignTransaction>['transaction'] & { type: number }
    const baseTransaction = {
      to: toAddress,
      value: ethers.toBeHex(amount),
      data: options?.data || '0x',
      chainId: Number(networkInfo.chainId),
      nonce: ethers.toBeHex(nonce),
      gasLimit: ethers.toBeHex(options?.gasLimit || 300000),
      type: 0,
    }

    if (options?.maxFeePerGas && options?.maxPriorityFeePerGas) {
      transaction = {
        ...baseTransaction,
        maxFeePerGas: ethers.toBeHex(options.maxFeePerGas),
        maxPriorityFeePerGas: ethers.toBeHex(options.maxPriorityFeePerGas),
        type: 2,
      }
    } else {
      transaction = {
        ...baseTransaction,
        gasPrice: ethers.toBeHex(feeData.gasPrice || '0'),
        type: 0,
      }
    }

    const result = await TrezorConnect.ethereumSignTransaction({ path, transaction })

    if (!result.success) {
      throw new Error(result.payload.error || 'Failed to sign transaction')
    }
    const tx = await this.provider.broadcastTransaction(result.payload.serializedTx)
    return tx.hash
  }

  private parsePath(path: string): number[] {
    return path
      .split('/')
      .map((part) => {
        if (part === 'm' || part === 'M' || part === '') return undefined

        if (part.endsWith("'")) {
          return (parseInt(part.slice(0, -1)) | 0x80000000) >>> 0
        }

        return parseInt(part)
      })
      .filter((n) => n !== undefined)
  }

  private getScriptTypeFromAddress(
    address: string,
    isInput: boolean
  ): BitcoinInputScriptType | BitcoinOutputScriptType {
    if (address.startsWith('bc1') || address.startsWith('tb1')) {
      return isInput ? 'SPENDWITNESS' : 'PAYTOWITNESS'
    }
    if (address.startsWith('3') || address.startsWith('2')) {
      return isInput ? 'SPENDP2SHWITNESS' : 'PAYTOP2SHWITNESS'
    }
    return isInput ? 'SPENDADDRESS' : 'PAYTOADDRESS'
  }

  private async sendBitcoinTransaction(
    toAddress: string,
    amount: number,
    networkId: string,
    options?: TransactionOptions
  ): Promise<string> {
    try {
      const { path, coin } = this.getNetworkParams(networkId, this.path)
      const address_n = this.parsePath(path)

      const feeRate = options?.feeRate || (await getBtcFeeRate(networkId))

      // Get current wallet address
      const currentAddress = await this.getAddress(networkId)

      // Get account info and select UTXOs
      const confirmedUtxos = await getConfirmedUtxos(networkId, currentAddress)
      const { selectedUtxos, totalValue, estimatedFee } = selectUtxos(
        confirmedUtxos,
        path,
        currentAddress,
        toAddress,
        amount,
        feeRate,
        options?.feeRateMultiplier
      )

      const change = totalValue - amount - estimatedFee

      // Prepare inputs
      const inputs: ProtoWithDerivationPath<PROTO.TxInputType>[] = selectedUtxos.map((utxo) => {
        const scriptType = this.getScriptTypeFromAddress(currentAddress, true) as BitcoinInputScriptType

        return {
          address_n,
          prev_index: utxo.vout,
          prev_hash: utxo.txid,
          amount: utxo.value.toString(),
          script_type: scriptType,
        }
      })

      // Prepare outputs
      const outputs: ProtoWithDerivationPath<PROTO.TxOutputType>[] = [
        {
          address: toAddress,
          amount: amount.toString(),
          script_type: this.getScriptTypeFromAddress(toAddress, false) as BitcoinOutputScriptType,
        },
      ]

      // Add change output if needed (dust threshold = 546 satoshis)
      if (change > 546) {
        const changeAddress = await this.getAddress(networkId)
        outputs.push({
          address_n,
          amount: change.toString(),
          script_type: this.getScriptTypeFromAddress(changeAddress, false) as BitcoinOutputScriptType,
        })
      }

      // We handle mainnet and testnet differently, because default backend api for trezor doesn't work for testnet V3
      if (networkId === SUPPORTED_NETWORK.BTC) {
        // Mainnet case, use default blockbook backend api from trezor
        const signedTx = await TrezorConnect.signTransaction({
          inputs,
          outputs,
          coin,
        })

        if (!signedTx.success) {
          throw new Error(signedTx.payload.error || 'Failed to sign transaction')
        }

        if (!signedTx.payload.serializedTx) {
          throw new Error('Signed transaction missing serializedTx')
        }

        const pushResult = await TrezorConnect.pushTransaction({
          tx: signedTx.payload.serializedTx,
          coin,
        })

        if (!pushResult.success) {
          throw new Error(pushResult.payload.error || 'Failed to broadcast transaction')
        }
        return pushResult.payload.txid
      } else {
        // Testnet case, we build refTxs on our own,
        // Becase default trezor blockbook api use testnet version 4, while we are working on version3
        // Need to upgrade to version 4 later
        // TODO: update with quicknode api
        const refTxs: RefTransaction[] = []
        const baseUrl =
          networkId === SUPPORTED_NETWORK.BTC ? 'https://mempool.space/api' : 'https://mempool.space/testnet/api'
        for (const utxo of selectedUtxos) {
          const rawTx = await fetch(`${baseUrl}/tx/${utxo.txid}`).then((res) => res.json())
          const inputRef: PROTO.PrevInput[] = rawTx.vin.map((value: any) => {
            return {
              prev_hash: value.txid,
              prev_index: value.vout,
              sequence: value.sequence,
              script_sig: value.scriptsig,
            }
          })
          const binOutputs: PROTO.TxOutputBinType[] = rawTx.vout.map((value: any) => {
            return {
              amount: value.value,
              script_pubkey: value.scriptpubkey,
            }
          })
          refTxs.push({
            hash: utxo.txid,
            version: Number(rawTx.version),
            inputs: inputRef,
            bin_outputs: binOutputs,
            lock_time: Number(rawTx.locktime),
          })
        }

        const signedTx = await TrezorConnect.signTransaction({
          inputs,
          outputs,
          coin,
          refTxs,
        })
        if (!signedTx.success) {
          throw new Error(signedTx.payload.error || 'Failed to sign transaction')
        }

        if (!signedTx.payload.serializedTx) {
          throw new Error('Signed transaction missing serializedTx')
        }
        return await broadcastTx(networkId, signedTx.payload.serializedTx)
      }
    } catch (error) {
      console.error('Bitcoin transaction failed:', error)
      throw error instanceof Error ? error : new Error('Unknown error during Bitcoin transaction')
    }
  }

  isInstalled(): boolean {
    return true
  }

  async getAddress(network: string): Promise<string> {
    const { path } = this.getNetworkParams(network, this.path)

    let result

    // Use ethereumGetAddress for all Ethereum networks
    if (isEvmChain(network)) {
      result = await TrezorConnect.ethereumGetAddress({
        path,
        showOnTrezor: false,
      })
    } else if (isBtcChain(network)) {
      // Use getAddress with coin parameter for Bitcoin networks
      const address = this.connectedAddresses[network]
      if (!address) {
        throw new Error('Wallet not connected for this network')
      }
      return address
    } else {
      throw new Error(`Unsupported network for getting address: ${network}`)
    }

    if (!result.success) {
      throw new Error(result.payload.error)
    }

    return result.payload.address
  }

  async signMessage(message: string, network: string): Promise<string> {
    if (!network) {
      throw new Error('Network must be specified for signing messages')
    }

    const { path, coin } = this.getNetworkParams(network, this.path)

    let result
    if (isEvmChain(network)) {
      result = await TrezorConnect.ethereumSignMessage({
        path,
        message,
      })
    } else if (isBtcChain(network)) {
      result = await TrezorConnect.signMessage({
        path,
        message,
        coin,
      })
    } else {
      throw new Error(`Unsupported network for message signing: ${network}`)
    }

    if (!result.success) {
      throw new Error(result.payload.error)
    }

    return result.payload.signature
  }

  async getCompressedPublicKey(network: string): Promise<string> {
    if (!network) {
      throw new Error('Network must be specified for getting compressed public key')
    }
    if (isEvmChain(network)) return this.connectedAddresses[network]
    if (isBtcChain(network)) return this.connectedCompressedPublicKey[network]

    throw new Error(`Unsupported network for getting compressed public key: ${network}`)
  }

  async signPsbt(_psbt: string, _network: string, _options?: PsbtSignOptions): Promise<string> {
    throw new Error('SignPsbt is not supported for Trezor Wallet')
  }

  priority(): number {
    return this.isInstalled() ? 0 : 1
  }
}
